import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Loader2, Eye, EyeOff } from "lucide-react";
import { useAuth, rolePrefix } from "../../lib/AuthContext";
import { ApiError } from "../../lib/api";

/* Login page. Email + password → POST /auth/login → cookies set →
   AuthContext.login() updates user state → redirect to role's home.

   If we got here from a guarded URL (via RouteGuard navigate with
   state.from), redirect back there post-login. Otherwise, dashboard. */
export function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const user = await login(email.trim().toLowerCase(), password);
      const dest = location.state?.from || `${rolePrefix(user.role)}/dashboard`;
      navigate(dest, { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't log in. Try again.");
      setBusy(false);
    }
  };

  return (
    <AuthLayout title="Sign in to AbhiGhar" subtitle="Use your work email">
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="text-xs font-medium text-slate-600 mb-1.5 block">Email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoFocus
            autoComplete="username"
            className="w-full text-sm rounded-lg ring-1 ring-inset ring-slate-300 px-3 py-2.5 focus:outline-none focus:ring-blue-500"
          />
        </div>
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-xs font-medium text-slate-600">Password</label>
            <Link to="/portal-x7k3/recover" className="text-xs text-blue-600 hover:text-blue-800">Forgot?</Link>
          </div>
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              className="w-full text-sm rounded-lg ring-1 ring-inset ring-slate-300 px-3 py-2.5 pr-10 focus:outline-none focus:ring-blue-500"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? "Hide password" : "Show password"}
              className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-slate-600"
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>
        {error && (
          <div className="text-sm text-red-700 bg-red-50 ring-1 ring-red-200 rounded-lg px-3 py-2">
            {error}
          </div>
        )}
        <button
          type="submit"
          disabled={busy}
          className="w-full bg-[#162a52] hover:bg-[#0f1f3d] disabled:opacity-60 text-white font-semibold rounded-lg px-4 py-2.5 text-sm inline-flex items-center justify-center gap-2 transition-colors"
        >
          {busy && <Loader2 className="w-4 h-4 animate-spin" />}
          {busy ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </AuthLayout>
  );
}

/* Shared auth-page chrome — branded sidebar + centered card.
   Exported so the other auth pages (Forgot, Reset, AcceptInvitation)
   can reuse it. */
export function AuthLayout({ title, subtitle, children }) {
  return (
    <div className="min-h-screen bg-slate-50 grid lg:grid-cols-2">
      {/* Left rail — brand panel. Hidden on small screens. */}
      <div className="hidden lg:flex bg-[#162a52] text-white p-12 flex-col justify-between">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-[#faf9f5] grid place-items-center overflow-hidden ring-1 ring-white/20 shrink-0">
            <img src="assets/abhighar-logo.png" alt="AbhiGhar" className="w-8 h-8 object-contain" />
          </div>
          <span className="text-2xl font-bold tracking-tight text-[#c79a3a]">AbhiGhar</span>
        </div>
        <div>
          <h1 className="text-3xl font-semibold leading-tight">
            Pune real estate,<br />without the broker spam.
          </h1>
          <p className="text-sm text-slate-300 mt-3">
            AbhiGhar
          </p>
        </div>
        <div className="text-xs text-slate-400">© 2026 AbhiGhar</div>
      </div>

      {/* Right rail — form card. */}
      <div className="flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex flex-col items-center mb-6">
            <div className="w-14 h-14 rounded-2xl bg-[#faf9f5] grid place-items-center overflow-hidden ring-1 ring-slate-200 shadow-sm mb-2">
              <img src="assets/abhighar-logo.png" alt="AbhiGhar" className="w-10 h-10 object-contain" />
            </div>
            <div className="text-xl font-bold tracking-tight text-[#162a52]">
              Abhi<span className="text-[#c79a3a]">Ghar</span>
            </div>
          </div>
          <div className="hidden lg:flex justify-center mb-6">
            <div className="w-28 h-28 rounded-3xl bg-[#faf9f5] grid place-items-center overflow-hidden ring-1 ring-slate-200 shadow-md">
              <img src="assets/abhighar-logo.png" alt="AbhiGhar" className="w-24 h-24 object-contain" />
            </div>
          </div>
          <h2 className="text-xl font-semibold text-slate-900 lg:text-center">{title}</h2>
          {subtitle && <p className="text-sm text-slate-500 mt-1 lg:text-center">{subtitle}</p>}
          <div className="mt-6">{children}</div>
        </div>
      </div>
    </div>
  );
}
export default Login;
